import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import LogoUploader from './LogoUploader';

const { FiAlertCircle, FiSave, FiX, FiRefreshCw, FiDownload, FiLoader, FiUpload } = FiIcons;

const CouponForm = ({ countries, selectedCountry, coupon, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    id: null,
    brand: '',
    title: '',
    description: '',
    discount: '',
    code: '',
    storeUrl: '',
    logoImage: '',
  });
  const [errors, setErrors] = useState({});
  const [isFetchingImages, setIsFetchingImages] = useState(false);
  const [fetchStatus, setFetchStatus] = useState({
    success: false,
    message: ''
  });
  const [showExcelUpload, setShowExcelUpload] = useState(false);
  const fileInputRef = React.useRef(null);

  // Set initial form data when editing
  useEffect(() => {
    if (coupon) {
      console.log("Editing coupon data:", coupon);
      setFormData({
        id: coupon.id,
        brand: coupon.brand || '',
        title: coupon.title || '',
        description: coupon.description || '',
        discount: coupon.discount || '',
        code: coupon.code || '',
        storeUrl: coupon.storeUrl || '', // Use storeUrl from component prop
        logoImage: coupon.logoImage || '', // Use logoImage from component prop
      });
    } else {
      // Generate random code for new coupons
      setFormData(prev => ({
        ...prev,
        id: null,
        code: `CODE${Math.random().toString(36).substring(2, 8).toUpperCase()}`
      }));
    }
  }, [coupon]);

  // Auto-fetch data when store URL changes
  useEffect(() => {
    // Only auto-fetch if there's a valid URL and we're missing logo
    if (formData.storeUrl && (formData.storeUrl.startsWith('http://') || formData.storeUrl.startsWith('https://')) && !formData.logoImage) {
      fetchImagesAndMetadataFromStoreUrl();
    }
  }, [formData.storeUrl]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleLogoChange = (logoImageData) => {
    setFormData(prev => ({ ...prev, logoImage: logoImageData }));
    
    // Clear logo error if it exists
    if (errors.logoImage) {
      setErrors(prev => ({ ...prev, logoImage: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.brand.trim()) newErrors.brand = 'Brand name is required';
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    if (!formData.discount.trim()) newErrors.discount = 'Discount is required';
    if (!formData.code.trim()) newErrors.code = 'Coupon code is required';
    if (!formData.storeUrl.trim()) newErrors.storeUrl = 'Store URL is required';
    if (!formData.logoImage) newErrors.logoImage = 'Brand logo is required';

    // URL validation
    if (formData.storeUrl && !isValidUrl(formData.storeUrl)) {
      newErrors.storeUrl = 'Please enter a valid URL';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  // Function to clean and validate URLs
  const cleanAndValidateUrl = (url) => {
    if (!url) return '';
    
    let cleanUrl = url.trim();
    
    // Check if URL has protocol, if not add it
    if (!cleanUrl.startsWith('http://') && !cleanUrl.startsWith('https://')) {
      cleanUrl = 'https://' + cleanUrl;
    }
    
    try {
      const urlObj = new URL(cleanUrl);
      return urlObj.href;
    } catch (e) {
      // If it's not a valid URL, try to extract URL parts
      const urlMatch = cleanUrl.match(/(https?:\/\/[^\s]+)/);
      if (urlMatch && urlMatch[0]) {
        try {
          const extractedUrl = new URL(urlMatch[0]);
          return extractedUrl.href;
        } catch (e) {
          return '';
        }
      }
      return '';
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      // Format data for saving to database
      const couponData = {
        id: formData.id,
        brand: formData.brand,
        title: formData.title,
        description: formData.description,
        discount: formData.discount,
        code: formData.code,
        store_url: cleanAndValidateUrl(formData.storeUrl), // Map to database field name
        logo_image: formData.logoImage, // Map to database field name
      };
      onSave(couponData);
    }
  };

  const generateRandomCode = () => {
    const newCode = `CODE${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
    setFormData(prev => ({ ...prev, code: newCode }));
  };

  // Function to auto-fetch images and metadata from store URL
  const fetchImagesAndMetadataFromStoreUrl = async () => {
    if (!formData.storeUrl || !isValidUrl(formData.storeUrl)) {
      setErrors(prev => ({ ...prev, storeUrl: 'Please enter a valid URL to fetch data' }));
      return;
    }

    setIsFetchingImages(true);
    setFetchStatus({ success: false, message: 'Fetching data...' });

    try {
      // Sanitize the URL
      const url = new URL(formData.storeUrl);
      const domain = url.hostname.replace('www.', '');

      // Extract domain name for brand name
      const domainParts = domain.split('.');
      let brandName = '';
      if (domainParts.length >= 2) {
        brandName = domainParts[domainParts.length - 2];
        // Capitalize first letter and format (e.g., "amazon" -> "Amazon")
        brandName = brandName.charAt(0).toUpperCase() + brandName.slice(1);
      }

      // Attempt to get the favicon
      const faviconUrl = `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;

      // Generate offer title based on domain name
      let offerTitle = `${brandName} Special Offer`;
      
      // Generate random discount between 10% and 50%
      const discountPercentage = Math.floor(Math.random() * 41) + 10; // 10% to 50%
      const discount = `${discountPercentage}%`;
      
      // Generate description based on domain and discount
      let description = `Save ${discount} on your purchase at ${brandName}. Limited time offer!`;
      
      // Enhanced descriptions based on domain categories (simplified version)
      if (domain.includes('fashion') || domain.includes('clothing') || domain.includes('apparel') || domain.includes('wear')) {
        offerTitle = `${brandName} Fashion Deal`;
        description = `Get ${discount} off on the latest fashion collection at ${brandName}. Includes clothing, accessories, and more!`;
      } else if (domain.includes('tech') || domain.includes('electronics') || domain.includes('gadget') || domain.includes('computer')) {
        offerTitle = `${brandName} Tech Sale`;
        description = `Save ${discount} on electronics, gadgets, and tech accessories at ${brandName}. Limited time offer!`;
      } else if (domain.includes('food') || domain.includes('restaurant') || domain.includes('eat') || domain.includes('dining')) {
        offerTitle = `${brandName} Dining Offer`;
        description = `Enjoy ${discount} off on your food order at ${brandName}. Valid for dine-in and takeaway!`;
      } else if (domain.includes('travel') || domain.includes('hotel') || domain.includes('flight') || domain.includes('booking')) {
        offerTitle = `${brandName} Travel Deal`;
        description = `Book your next trip with ${brandName} and save ${discount} on hotels, flights, and vacation packages!`;
      } else if (domain.includes('beauty') || domain.includes('cosmetic') || domain.includes('makeup')) {
        offerTitle = `${brandName} Beauty Sale`;
        description = `Get ${discount} off on beauty products, skincare, and cosmetics at ${brandName}. Shop now!`;
      }

      // Verify the logo image is accessible
      const logoPromise = new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(true);
        img.onerror = () => resolve(false);
        img.src = faviconUrl;
      });

      const logoSuccess = await logoPromise;

      // Update form data with fetched images and metadata
      setFormData(prev => ({
        ...prev,
        brand: brandName || prev.brand,
        title: offerTitle || prev.title,
        description: description || prev.description,
        discount: discount || prev.discount,
        logoImage: logoSuccess ? faviconUrl : prev.logoImage,
      }));

      setFetchStatus({
        success: true,
        message: `Successfully fetched data from ${brandName}`
      });
    } catch (error) {
      console.error('Error fetching data:', error);
      setFetchStatus({
        success: false,
        message: 'Failed to fetch data. Please fill in the fields manually.'
      });
    } finally {
      setIsFetchingImages(false);
      // Auto-hide the message after 4 seconds
      setTimeout(() => {
        setFetchStatus({ success: false, message: '' });
      }, 4000);
    }
  };

  const handleExcelUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel', // .xls
      'text/csv', // .csv
      'application/json' // .json
    ];

    if (!allowedTypes.includes(file.type)) {
      setErrors(prev => ({ ...prev, excel: 'Please upload an Excel, CSV, or JSON file' }));
      return;
    }

    setFetchStatus({ success: false, message: 'Reading file data...' });

    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        let data;

        // For CSV files
        if (file.type === 'text/csv') {
          // Parse CSV using a simple approach
          const text = e.target.result;
          const rows = text.split('\n');
          if (rows.length < 2) {
            throw new Error('CSV file appears to be empty or invalid');
          }

          // Get headers
          const headers = rows[0].split(',').map(h => h.trim().toLowerCase());

          // Check for required fields
          const requiredFields = ['brand', 'title', 'discount', 'description'];
          const missingFields = requiredFields.filter(field => !headers.includes(field));
          if (missingFields.length > 0) {
            throw new Error(`CSV is missing required columns: ${missingFields.join(', ')}`);
          }

          // Get data from first row (after header)
          const dataRow = rows[1].split(',').map(cell => cell.trim());

          // Create object from headers and data
          data = {};
          headers.forEach((header, index) => {
            if (index < dataRow.length) {
              data[header] = dataRow[index];
            }
          });
        }
        // For JSON files
        else if (file.type === 'application/json') {
          const text = e.target.result;
          const jsonData = JSON.parse(text);

          // If it's an array, take the first item
          if (Array.isArray(jsonData) && jsonData.length > 0) {
            data = jsonData[0];
          }
          // If it's an object with country codes as keys
          else if (typeof jsonData === 'object' && !Array.isArray(jsonData)) {
            // Try to find data for the selected country
            const countryData = jsonData[selectedCountry?.code];
            if (Array.isArray(countryData) && countryData.length > 0) {
              data = countryData[0];
            } else {
              // Take the first country's first item
              const firstCountryKey = Object.keys(jsonData)[0];
              if (Array.isArray(jsonData[firstCountryKey]) && jsonData[firstCountryKey].length > 0) {
                data = jsonData[firstCountryKey][0];
              } else {
                // Just use the entire object as data
                data = jsonData;
              }
            }
          } else {
            // Just use the entire object as data
            data = jsonData;
          }
        }
        // For Excel files - now supported with xlsx library
        else if (file.type.includes('excel')) {
          const arrayBuffer = e.target.result;
          const XLSX = await import('xlsx');
          const workbook = XLSX.read(arrayBuffer, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);

          if (jsonData.length > 0) {
            data = jsonData[0];
          } else {
            throw new Error('Excel file appears to be empty or invalid');
          }
        }

        // Extract store URL correctly without adding brand name
        let storeUrl = data?.store_url || data?.storeurl || data?.url || '';
        
        // Make sure we only get the URL without any brand name
        let cleanStoreUrl = cleanAndValidateUrl(storeUrl);

        // Update form data with the parsed data
        if (data) {
          setFormData(prev => ({
            ...prev,
            brand: data.brand || data.brand_name || prev.brand,
            title: data.title || data.offer_title || prev.title,
            description: data.description || prev.description,
            discount: data.discount || prev.discount,
            code: data.code || data.coupon_code || prev.code,
            storeUrl: cleanStoreUrl || prev.storeUrl,
            logoImage: data.logoimage || data.logo_image || data.logo || prev.logoImage,
          }));

          setFetchStatus({
            success: true,
            message: 'Successfully imported data from file'
          });

          // Auto-hide the message after 4 seconds
          setTimeout(() => {
            setFetchStatus({ success: false, message: '' });
          }, 4000);
        } else {
          throw new Error('Could not find valid data in the file');
        }
      } catch (error) {
        console.error('Error processing file:', error);
        setFetchStatus({
          success: false,
          message: `Failed to process file: ${error.message}`
        });

        // Auto-hide the message after 4 seconds
        setTimeout(() => {
          setFetchStatus({ success: false, message: '' });
        }, 4000);
      }
    };

    reader.onerror = () => {
      setFetchStatus({ success: false, message: 'Failed to read file' });
    };

    if (file.type === 'text/csv' || file.type === 'application/json') {
      reader.readAsText(file);
    } else {
      reader.readAsArrayBuffer(file); // For Excel files
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="p-5 overflow-auto max-h-[calc(90vh-80px)]"
    >
      <form onSubmit={handleSubmit}>
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            {coupon ? 'Edit Coupon' : 'Add New Coupon'}
          </h3>
          <p className="text-sm text-gray-600">
            {coupon ? `Editing coupon for ${selectedCountry?.name}` : `Creating new coupon for ${selectedCountry?.name}`}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Store URL with Auto-Fetch Button */}
          <div className="md:col-span-2">
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-gray-700">
                Store URL *
              </label>
              <div className="flex space-x-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowExcelUpload(!showExcelUpload);
                  }}
                  className="text-xs flex items-center space-x-1 text-blue-600 hover:text-blue-800"
                >
                  <SafeIcon icon={FiUpload} className="text-xs" />
                  <span>Import from file</span>
                </button>
              </div>
            </div>

            {showExcelUpload && (
              <div className="mb-3 p-3 border border-blue-100 bg-blue-50 rounded-lg">
                <div className="text-sm mb-2 text-blue-800 font-medium">Import data from file</div>
                <div className="flex">
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept=".csv,.json,.xlsx,.xls"
                    onChange={handleExcelUpload}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                </div>
                <p className="mt-1 text-xs text-blue-600">
                  Supported formats: CSV, JSON, Excel (.xlsx, .xls)
                </p>
              </div>
            )}

            <div className="flex">
              <input
                type="text"
                name="storeUrl"
                value={formData.storeUrl}
                onChange={handleChange}
                placeholder="https://store.example.com"
                className={`flex-1 p-2 border rounded-l-lg ${errors.storeUrl ? 'border-red-500' : 'border-gray-300'}`}
              />
              <button
                type="button"
                onClick={fetchImagesAndMetadataFromStoreUrl}
                disabled={isFetchingImages}
                className={`px-3 py-2 rounded-r-lg flex items-center space-x-1 ${
                  isFetchingImages
                    ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                    : 'bg-blue-500 hover:bg-blue-600 text-white'
                }`}
                title="Auto-fetch data from store URL"
              >
                <SafeIcon
                  icon={isFetchingImages ? FiRefreshCw : FiDownload}
                  className={isFetchingImages ? "animate-spin" : ""}
                />
                <span className="hidden sm:inline">Fetch Data</span>
              </button>
            </div>
            {errors.storeUrl && (
              <p className="mt-1 text-sm text-red-600 flex items-center">
                <SafeIcon icon={FiAlertCircle} className="mr-1" />
                {errors.storeUrl}
              </p>
            )}
            {fetchStatus.message && (
              <p
                className={`mt-1 text-sm flex items-center ${
                  fetchStatus.success
                    ? 'text-green-600'
                    : fetchStatus.message.includes('Fetching') || fetchStatus.message.includes('Reading')
                    ? 'text-blue-600'
                    : 'text-orange-500'
                }`}
              >
                <SafeIcon
                  icon={
                    fetchStatus.success
                      ? FiIcons.FiCheckCircle
                      : fetchStatus.message.includes('Fetching') || fetchStatus.message.includes('Reading')
                      ? FiRefreshCw
                      : FiIcons.FiInfo
                  }
                  className={`mr-1 ${
                    fetchStatus.message.includes('Fetching') || fetchStatus.message.includes('Reading')
                      ? 'animate-spin'
                      : ''
                  }`}
                />
                {fetchStatus.message}
              </p>
            )}
          </div>

          {/* Brand Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Brand Name *
            </label>
            <input
              type="text"
              name="brand"
              value={formData.brand}
              onChange={handleChange}
              placeholder="e.g., Nike, Amazon, etc."
              className={`w-full p-2 border rounded-lg ${errors.brand ? 'border-red-500' : 'border-gray-300'}`}
            />
            {errors.brand && (
              <p className="mt-1 text-sm text-red-600 flex items-center">
                <SafeIcon icon={FiAlertCircle} className="mr-1" />
                {errors.brand}
              </p>
            )}
          </div>

          {/* Brand Logo Uploader */}
          <div>
            <LogoUploader currentImageUrl={formData.logoImage} onImageChange={handleLogoChange} />
            {errors.logoImage && (
              <p className="mt-1 text-sm text-red-600 flex items-center">
                <SafeIcon icon={FiAlertCircle} className="mr-1" />
                {errors.logoImage}
              </p>
            )}
          </div>

          {/* Offer Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Offer Title *
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="e.g., Summer Sale, Flash Discount"
              className={`w-full p-2 border rounded-lg ${errors.title ? 'border-red-500' : 'border-gray-300'}`}
            />
            {errors.title && (
              <p className="mt-1 text-sm text-red-600 flex items-center">
                <SafeIcon icon={FiAlertCircle} className="mr-1" />
                {errors.title}
              </p>
            )}
          </div>

          {/* Discount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Discount *
            </label>
            <input
              type="text"
              name="discount"
              value={formData.discount}
              onChange={handleChange}
              placeholder="e.g., 20%, 50 SAR off"
              className={`w-full p-2 border rounded-lg ${errors.discount ? 'border-red-500' : 'border-gray-300'}`}
            />
            {errors.discount && (
              <p className="mt-1 text-sm text-red-600 flex items-center">
                <SafeIcon icon={FiAlertCircle} className="mr-1" />
                {errors.discount}
              </p>
            )}
          </div>

          {/* Description */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description *
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Describe the offer in detail"
              rows="3"
              className={`w-full p-2 border rounded-lg ${errors.description ? 'border-red-500' : 'border-gray-300'}`}
            ></textarea>
            {errors.description && (
              <p className="mt-1 text-sm text-red-600 flex items-center">
                <SafeIcon icon={FiAlertCircle} className="mr-1" />
                {errors.description}
              </p>
            )}
          </div>

          {/* Coupon Code */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Coupon Code *
            </label>
            <div className="flex">
              <input
                type="text"
                name="code"
                value={formData.code}
                onChange={handleChange}
                placeholder="e.g., SUMMER20"
                className={`flex-1 p-2 border rounded-l-lg ${errors.code ? 'border-red-500' : 'border-gray-300'}`}
              />
              <button
                type="button"
                onClick={generateRandomCode}
                className="bg-gray-100 hover:bg-gray-200 px-3 rounded-r-lg border-t border-r border-b border-gray-300"
              >
                Random
              </button>
            </div>
            {errors.code && (
              <p className="mt-1 text-sm text-red-600 flex items-center">
                <SafeIcon icon={FiAlertCircle} className="mr-1" />
                {errors.code}
              </p>
            )}
          </div>
        </div>

        <div className="mt-6 flex items-center justify-end space-x-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors flex items-center space-x-1"
          >
            <SafeIcon icon={FiX} />
            <span>Cancel</span>
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors flex items-center space-x-1"
          >
            <SafeIcon icon={FiSave} />
            <span>{coupon ? 'Update' : 'Create'}</span>
          </button>
        </div>
      </form>
    </motion.div>
  );
};

export default CouponForm;